import React from "react"
import PropTypes from "prop-types"
import { Form, DatePicker, Input, Row, Col } from "antd"
import { PhoneInput } from "ui/Input/PhoneInput"
import styles from "../../index.module.css"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"

export const ParentGuardianForm = ({ form, parentData, setParentData, t, validatePhoneNumber, setIsFormValid, showRelation = false }) => {
  const handlePhoneChange = React.useCallback(
    (value) => {
      setParentData((prev) => ({ ...prev, phone: value }))
    },
    [setParentData]
  )

  const ageValidator = React.useCallback((_, value) => {
    if (!value) return Promise.resolve()

    const today = new Date()
    const birthDate = new Date(value)
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    const dayDiff = today.getDate() - birthDate.getDate()

    const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age

    if (actualAge < 18) {
      return Promise.reject("Date of birth should be greater than 18 years")
    }

    return Promise.resolve()
  }, [])

  const phoneValidator = React.useCallback(
    (_, value) => {
      if (!value?.fullNumber?.trim()) {
        return Promise.reject(t("form_validation.phone_required"))
      }
      return validatePhoneNumber(value?.fullNumber)
    },
    [t, validatePhoneNumber]
  )

  // Add onFieldsChange prop to Form
  return (
    <>
      <div className={`${styles.subTitle} mb-l`}>{t("embedded.booking_confirmation.parent_or_guardian")}</div>
      <Form
        layout="vertical"
        form={form}
        onFieldsChange={() => {
          if (form?.getFieldsValue && typeof setIsFormValid === "function") {
            const requiredFields = ["firstName", "lastName", "email", "dob", "phone", ...(showRelation ? ["relation"] : [])]
            const values = form.getFieldsValue()
            // Check age >= 18
            let valid = requiredFields.every((field) => {
              const value = values[field]
              if (field === "phone") {
                return value?.fullNumber?.trim()
              }
              return value?.toString()?.trim()
            })
            if (valid && values.dob) {
              const today = new Date()
              const birthDate = new Date(values.dob)
              const age = today.getFullYear() - birthDate.getFullYear()
              const monthDiff = today.getMonth() - birthDate.getMonth()
              const dayDiff = today.getDate() - birthDate.getDate()
              const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age
              valid = actualAge >= 18
            }
            setIsFormValid(valid)
          }
        }}
      >
        <Row gutter={[16, 16]} className={styles.formRow}>
          <Col md={12} span={24}>
            <LabeledField
              labelKey={t("embedded.booking_confirmation.first_name")}
              name="firstName"
              rules={[{ required: true, message: "Enter your first name" }]}
            >
              <Input placeholder="Enter your first name" />
            </LabeledField>
          </Col>
          <Col md={12} span={24}>
            <LabeledField
              labelKey={t("embedded.booking_confirmation.last_name")}
              name="lastName"
              rules={[{ required: true, message: "Enter your last name" }]}
            >
              <Input placeholder="Enter your last name" />
            </LabeledField>
          </Col>
          <Col md={12} span={24}>
            <LabeledField
              labelKey={t("embedded.booking_confirmation.email")}
              name="email"
              rules={[{ required: true, message: "Enter your email" }]}
            >
              <Input placeholder="example@email.com" />
            </LabeledField>
          </Col>
          <Col md={12} span={24}>
            <LabeledField
              labelKey={t("embedded.booking_confirmation.dob")}
              name="dob"
              rules={[{ required: true, message: "Enter your date of birth" }, { validator: ageValidator }]}
            >
              <DatePicker className="w-full p-xs" style={{ width: "100%" }} placeholder="MM/DD/YYYY" format="MM/DD/YYYY" />
            </LabeledField>
          </Col>
          <Col md={12} span={24}>
            <LabeledField labelKey={t("embedded.booking_confirmation.phone_number")} name="phone" rules={[{ validator: phoneValidator }]}>
              <PhoneInput
                initialCountryCode={parentData?.phone?.countryCode}
                initialValue={parentData?.phone?.fullNumber}
                onChange={handlePhoneChange}
              />
            </LabeledField>
          </Col>
          {showRelation && (
            <Col md={12} span={24}>
              <LabeledField
                labelKey="Relation*"
                name="relation"
                rules={[{ required: true, message: "Enter your relation to the minor" }]}
              >
                <Input placeholder="e.g. Parent, Sister, Brother" />
              </LabeledField>
            </Col>
          )}
        </Row>
      </Form>
    </>
  )
}

ParentGuardianForm.propTypes = {
  form: PropTypes.shape({
    getFieldsValue: PropTypes.func.isRequired
  }).isRequired,
  parentData: PropTypes.shape({
    phone: PropTypes.shape({
      countryCode: PropTypes.string,
      fullNumber: PropTypes.string
    })
  }).isRequired,
  setParentData: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  validatePhoneNumber: PropTypes.func.isRequired,
  setIsFormValid: PropTypes.func.isRequired,
  showRelation: PropTypes.bool
}
